import { useAlertsStore } from '@shared/hooks/state/AlertsContainer';
import { useAuthStore } from '@shared/hooks/state/AuthContainer';
import { useCurrentUserStore } from '@shared/hooks/state/CurrentUserContainer';
import IContentDialogProps from '@shared/models/components/IContentDialogProps';
import { LocalizationModelKeys } from '@shared/models/localization/Locales';
import UpdateUserLocaleRequest from '@shared/models/users/UpdateUserLocaleRequest';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardStackLayout } from '../core/card/CommonCardLayouts';
import ContentDialog from '../core/dialog/ContentDialog';
import LocalizationFieldCard from './LocalizationFieldCard';

interface ILocalizationDialogProps extends Omit<IContentDialogProps, 'title' | 'onConfirm' | 'onCancel' | 'onClose'> {
    onClose: () => void;
    customSave?: ((value: string) => void) | null | undefined;
}

function LocalizationEditDialog({
    open,
    onClose,
    description,
    customSave = null,
    ...restProps
}: ILocalizationDialogProps) {
    const { t } = useTranslation();
    const initialUserLocalePreferences = useCurrentUserStore((state) => state.userLocalePreferences);
    const getCurrentUserLocales = (): UpdateUserLocaleRequest => {
        return {
            defaultLocale: initialUserLocalePreferences.defaultLocale,
            defaultVoiceLocale: initialUserLocalePreferences.defaultVoiceLocale,
        };
    };
    const [userLocalePreferences, setUserLocalePreferences] =
        useState<UpdateUserLocaleRequest>(getCurrentUserLocales());

    const handleCancel = () => {
        onClose();
        setUserLocalePreferences(getCurrentUserLocales());
    };

    const handleSave = async () => {
        if (useAuthStore.getState().isAuthenticated) {
            const result = await useCurrentUserStore.getState().saveUserLocales(userLocalePreferences);

            if (result?.resultType == 'Ok') {
                onClose();
                useAlertsStore.getState().showSuccess(t('common:localization.save.success'), 5000);
            } else {
                useAlertsStore.getState().showError(t('common:localization.save.error'), 5000);
            }
        } else {
            // handle case when locale is changed pre-auth.
            useCurrentUserStore.getState().updateUserLocales(userLocalePreferences, false);
            onClose();
            useAlertsStore.getState().showSuccess(t('common:localization.save.success'), 5000);
        }
    };

    const handleLanguageSelection = (variant: LocalizationModelKeys, value: string) => {
        const newLocalePreferences: UpdateUserLocaleRequest = { ...userLocalePreferences };

        if (variant == 'display') {
            newLocalePreferences.defaultLocale = value;
        } else if (variant == 'voice') {
            newLocalePreferences.defaultVoiceLocale = value;
        }

        setUserLocalePreferences(newLocalePreferences);
    };

    // Sync local state when dialog opens or store values change
    useEffect(() => {
        if (open) {
            setUserLocalePreferences(getCurrentUserLocales());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialUserLocalePreferences.defaultLocale, initialUserLocalePreferences.defaultVoiceLocale]);

    return (
        <ContentDialog
            data-testid="localization-edit-dialog"
            title={t('common:localization.title')}
            description={description ?? ''}
            open={open}
            onConfirm={() => (customSave ? customSave(userLocalePreferences.defaultLocale) : handleSave())}
            onCancel={handleCancel}
            onClose={handleCancel}
            {...restProps}>
            <CardStackLayout>
                <LocalizationFieldCard
                    variant="display"
                    value={userLocalePreferences.defaultLocale}
                    onChange={(value) => handleLanguageSelection('display', value)}
                />
                <LocalizationFieldCard
                    variant="voice"
                    value={userLocalePreferences.defaultVoiceLocale}
                    onChange={(value) => handleLanguageSelection('voice', value)}
                />
            </CardStackLayout>
        </ContentDialog>
    );
}

export default LocalizationEditDialog;
